import classNames from "classnames";
import { Link } from "components/DesignSystem/Link/Link";
import { SmallRightArrow } from "components/icons/SmallRightArrow";
import { CertificateWatermark } from "components/icons/CertificateWatermark";
import { Info } from "components/icons/Info";
import UnitedStatesMap from "static/images/UnitedStatesMap.svg";
import { NexusTrackerSummaryResponse } from "store/apis/salesTax";
import { pluralize } from "utils/pluralize";
import ToolTip from "components/design/toolTip";
import { statusMapping } from "./SalesNexusTracker";

type NexusTrackerSummaryCardsProps = {
  summary: NexusTrackerSummaryResponse;
  registrationsUrl: string;
  onFilterByStatus: (status: keyof typeof statusMapping) => void;
};

type TrackedSegmentKey =
  | keyof typeof statusMapping
  | "REGISTERING"
  | "REMITTING";

type TrackedSegment = {
  key: TrackedSegmentKey;
  label: string;
  count: number;
  dotClassName: string;
};

type RegisteredSegment = {
  key: string;
  label: string;
  count: number;
  dotClassName: string;
};

const getTrackedSegments = (
  statusCounts: NexusTrackerSummaryResponse["status_counts"]
): TrackedSegment[] => [
  {
    key: "EXPOSED",
    label: "need action",
    count: statusCounts.EXPOSED,
    dotClassName: "t-bg-red",
  },
  {
    key: "APPROACHING",
    label: "approaching",
    count: statusCounts.APPROACHING,
    dotClassName: "t-bg-orange-40",
  },
  {
    key: "REGISTERING",
    label: "registering",
    count: statusCounts.REGISTERING,
    dotClassName: "t-bg-blue-50",
  },
  {
    key: "REMITTING",
    label: "filing active",
    count: statusCounts.REMITTING,
    dotClassName: "t-bg-dark_green-50",
  },
  {
    key: "MONITORING",
    label: "tracking",
    count: statusCounts.MONITORING,
    dotClassName: "t-bg-neutral-20",
  },
];

const getRegisteredSegments = (
  registrationStatusCounts: NexusTrackerSummaryResponse["registration_status_counts"]
): RegisteredSegment[] => [
  {
    key: "PENDING_DETAILS",
    label: "not started",
    count: registrationStatusCounts.PENDING_DETAILS,
    dotClassName: "t-bg-red",
  },
  {
    key: "PROCESSING",
    label: "processing",
    count: registrationStatusCounts.PROCESSING,
    dotClassName: "t-bg-orange-40",
  },
];

const CLICKABLE_SEGMENT_KEYS: TrackedSegmentKey[] = [
  "EXPOSED",
  "APPROACHING",
  "MONITORING",
  "REMITTING",
];

const EXEMPTED_TOOLTIP_NAME_LIMIT = 2;

const getExemptedTooltipText = (exemptedStates: string[]) => {
  if (exemptedStates.length > EXEMPTED_TOOLTIP_NAME_LIMIT) {
    return (
      <p className="t-m-0">
        {pluralize(exemptedStates.length, "state", "states")} not required
        <br /> as no sales tax is applicable to them
      </p>
    );
  }

  const isSingleState = exemptedStates.length === 1;

  return (
    <p className="t-m-0">
      {exemptedStates.join(" and ")} not required
      <br /> as no sales tax is applicable to {isSingleState ? "it" : "them"}
    </p>
  );
};

const TrackedStatesCard = ({
  summary,
  onFilterByStatus,
}: {
  summary: NexusTrackerSummaryResponse;
  onFilterByStatus: (status: keyof typeof statusMapping) => void;
}) => {
  const { tracked_count: trackedCount, exempted_states: exemptedStates } =
    summary;
  const segments = getTrackedSegments(summary.status_counts);

  return (
    <div className="t-relative t-flex-[3] t-overflow-hidden t-rounded-lg t-border t-border-solid t-border-neutral-0 t-p-5">
      <img
        src={UnitedStatesMap}
        alt=""
        className="t-pointer-events-none t-absolute t-right-0 t-top-0 t-z-0 t-h-full"
      />

      <div className="t-relative t-z-10 t-flex t-flex-col t-gap-4">
        <p className="t-m-0 t-flex t-items-center t-gap-1.5 t-text-title-h3 t-text-text-60">
          {trackedCount}{" "}
          <span className="t-text-subtext-sm t-text-text-30">
            {pluralize(trackedCount, "state", "states", false)} tracked
          </span>
          {exemptedStates.length > 0 && (
            <ToolTip text={getExemptedTooltipText(exemptedStates)}>
              <span className="t-flex t-items-center t-text-text-30">
                <Info size="14" />
              </span>
            </ToolTip>
          )}
        </p>

        <div className="t-flex t-h-2 t-w-full t-overflow-hidden t-rounded-full t-bg-neutral-0">
          {segments.map((segment) => (
            <div
              key={segment.key}
              className={classNames("t-h-full", segment.dotClassName)}
              style={{
                width: trackedCount
                  ? `${(segment.count / trackedCount) * 100}%`
                  : 0,
              }}
            />
          ))}
        </div>

        <div className="t-flex t-flex-wrap t-items-center t-gap-4">
          {segments
            .filter((segment) => segment.count > 0)
            .map((segment) => {
              const isClickable = CLICKABLE_SEGMENT_KEYS.includes(segment.key);

              return isClickable ? (
                <button
                  key={segment.key}
                  type="button"
                  onClick={() =>
                    onFilterByStatus(segment.key as keyof typeof statusMapping)
                  }
                  className="all:unset t-flex t-cursor-pointer t-items-center t-gap-1.5 t-text-body-sm t-text-text-60 hover:t-text-text-100"
                >
                  <span
                    className={classNames(
                      "t-h-1.5 t-w-1.5 t-rounded-full",
                      segment.dotClassName
                    )}
                  />
                  {segment.count} {segment.label}
                </button>
              ) : (
                <span
                  key={segment.key}
                  className="t-flex t-items-center t-gap-1.5 t-text-body-sm t-text-text-60"
                >
                  <span
                    className={classNames(
                      "t-h-1.5 t-w-1.5 t-rounded-full",
                      segment.dotClassName
                    )}
                  />
                  {segment.count} {segment.label}
                </span>
              );
            })}
        </div>
      </div>
    </div>
  );
};

const RegisteredStatesCard = ({
  summary,
  registrationsUrl,
}: {
  summary: NexusTrackerSummaryResponse;
  registrationsUrl: string;
}) => {
  const { tracked_count: trackedCount, registered_count: registeredCount } =
    summary;
  const segments = getRegisteredSegments(summary.registration_status_counts);

  return (
    <div className="t-relative t-flex-[2] t-overflow-hidden t-rounded-lg t-border t-border-solid t-border-neutral-0 t-p-5">
      <CertificateWatermark className="t-pointer-events-none t-absolute t-right-0 t-top-0 t-z-0" />

      <div className="t-relative t-z-10 t-flex t-flex-col t-gap-4">
        <p className="t-m-0 t-text-title-h3 t-text-text-60">
          {registeredCount}
          <span className="t-text-subtext-sm t-text-text-30">
            /{trackedCount} states registered
          </span>
        </p>

        <div className="t-flex t-flex-wrap t-items-center t-gap-4">
          {segments
            .filter((segment) => segment.count > 0)
            .map((segment) => (
              <span
                key={segment.key}
                className="t-flex t-items-center t-gap-1.5 t-text-body-sm t-text-text-60"
              >
                <span
                  className={classNames(
                    "t-h-1.5 t-w-1.5 t-rounded-full",
                    segment.dotClassName
                  )}
                />
                {segment.count} {segment.label}
              </span>
            ))}
        </div>

        <Link
          to={registrationsUrl}
          className="t-flex t-items-center t-gap-1 t-text-body-sm !t-text-text-30"
        >
          View <SmallRightArrow />
        </Link>
      </div>
    </div>
  );
};

export const NexusTrackerSummaryCards = ({
  summary,
  registrationsUrl,
  onFilterByStatus,
}: NexusTrackerSummaryCardsProps) => {
  return (
    <div className="t-flex t-w-full t-gap-4 sm:t-flex-col">
      <TrackedStatesCard
        summary={summary}
        onFilterByStatus={onFilterByStatus}
      />
      <RegisteredStatesCard
        summary={summary}
        registrationsUrl={registrationsUrl}
      />
    </div>
  );
};
